"use client"

import { useState, useEffect } from 'react'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useToast } from '@/hooks/use-toast'
import TaxesService from '@/api/taxes.service'

interface TaxType {
  id: number
  name: string
  code: string
  description: string
}

interface TaxTypeSelectorProps {
  onSelect: (taxType: TaxType | null) => void
  value?: number | null
  placeholder?: string
  disabled?: boolean
}

export function TaxTypeSelector({ onSelect, value, placeholder = "Select tax type", disabled = false }: TaxTypeSelectorProps) {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([])
  const [selectedTaxType, setSelectedTaxType] = useState<TaxType | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    const fetchTaxTypes = async () => {
      if (taxTypes.length > 0) return // Don't refetch if we already have data
      
      setIsLoading(true)
      try {
        const response = await TaxesService.getAllTaxTypes()
        if (response.data.result === 'success') {
          setTaxTypes(response.data.taxTypes)
          
          // If a value is already selected, find and set it
          if (value) {
            const selected = response.data.taxTypes.find((type: TaxType) => type.id === value)
            if (selected) {
              setSelectedTaxType(selected)
            }
          }
        } else {
          toast({
            title: 'Error',
            description: response.data.message || 'Failed to load tax types',
            variant: 'destructive',
          })
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load tax types. Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
      }
    }
    
    fetchTaxTypes()
  }, [toast, value, taxTypes.length])
  
  const handleSelect = (taxType: TaxType) => {
    setSelectedTaxType(taxType)
    onSelect(taxType)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <div className="flex items-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : selectedTaxType ? (
            <div className="flex items-center">
              <span>{selectedTaxType.name}</span>
              {selectedTaxType.code && (
                <span className="ml-2 text-xs bg-slate-100 py-0.5 px-1.5 rounded text-slate-700">
                  {selectedTaxType.code}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search tax types..." />
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <CommandList>
              <CommandEmpty>No tax type found.</CommandEmpty>
              <CommandGroup>
                {taxTypes.map((taxType) => (
                  <CommandItem
                    key={taxType.id}
                    value={`${taxType.id}-${taxType.name}`}
                    onSelect={() => handleSelect(taxType)}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <span>{taxType.name}</span>
                      {taxType.code && (
                        <span className="ml-2 text-xs bg-slate-100 py-0.5 px-1.5 rounded text-slate-700">
                          {taxType.code}
                        </span>
                      )}
                    </div>
                    {selectedTaxType?.id === taxType.id && <Check className="h-4 w-4 text-primary" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
} 